'use client';

import {
  ISalePageDetailResponseProduct,
  ISalePageProductAdditionalInformResponse,
} from '@/api/types';
import { Button, CTA } from '@/components';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import {
  AnimatedNumber,
  Column,
  Element,
  Icon,
  Row,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@vuddy/components';
import { getClientSideUserAgent, isIOSMobile } from '@vuddy/utils';
import clsx from 'clsx';
import { ChangeEvent, useEffect } from 'react';
import * as styles from '../common/bottom.css';
import { ConnectedVerificationCodeModal } from '../common/ConnectedVerificationCodeModal';
import { DigitalItemList } from './DigitalItemList';
import { useDigitalPurchaseLogic } from './useDigitalPurchaseLogic';

interface PurchaseBottomSheetProps {
  salePageId: number;
  interactive?: boolean;
  onClose?: VoidFunction;
  /** CartContext에서 전달받은 초기 선택값 (바텀시트는 CartProvider 밖에서 렌더됨) */
  initialSelectedId?: number | null;
  /** CartContext의 setSelectedId. 선택 변경 시 외부에 동기화. */
  syncSelectedId?: (id: number | null) => void;
  /** CartContext에서 전달받은 초기 복수 선택값 (웹 전용) */
  initialSelectedIds?: number[];
  /** CartContext의 복수 선택 setter. 선택 변경 시 외부에 동기화. */
  syncSelectedIds?: (ids: number[] | ((prev: number[]) => number[])) => void;
}

export const DigitalPurchaseBottomSheet = ({
  salePageId,
  interactive = true,
  onClose,
  initialSelectedId,
  syncSelectedId,
  initialSelectedIds,
  syncSelectedIds,
}: PurchaseBottomSheetProps) => {
  const device = useDeviceSize();

  const {
    itemList,
    isClass,
    isAppWebView,
    selectedId,
    selectedIds,
    selectedProducts,
    totalSelectedAmount,
    setSelectedId,
    toggleSelectedId,
    verification,
    handleCTAClick,
    handleAddToCartClick,
  } = useDigitalPurchaseLogic({
    salePageId,
    initialSelectedId,
    syncSelectedId,
    initialSelectedIds,
    syncSelectedIds,
    onClose,
  });

  // 모바일 사이즈가 아닌 경우 닫기
  useEffect(() => {
    if (device && device !== 'mobile') {
      onClose?.();
    }
  }, [device, onClose]);

  const useVisualViewportLayout =
    device === 'mobile' &&
    !isAppWebView &&
    isIOSMobile(getClientSideUserAgent());

  return (
    <Column className={styles.wrapper({ interactive })}>
      <Column
        className={clsx(
          useVisualViewportLayout && styles.visualViewportBottomSheet
        )}>
        <Element
          className={clsx(
            styles.bottomSheetContents,
            useVisualViewportLayout && styles.visualViewportBottomSheetContents
          )}>
          <DigitalItemList
            itemList={
              itemList as (ISalePageDetailResponseProduct &
                ISalePageProductAdditionalInformResponse)[]
            }
            selectedId={selectedId || undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSelectedId(Number(e.target.value));
            }}
            onCodeInputClick={itemId => {
              verification.openVerificationModal(itemId);
            }}
            verifiedProductCodes={verification.verifiedProductCodes}
            selectionMode={isAppWebView ? 'single' : 'multiple'}
            selectedIds={selectedIds}
            onToggle={toggleSelectedId}
          />
        </Element>
        <CTA
          direction="vertical"
          position="sticky"
          description={
            <Row justifyContent="space-between">
              <Tooltip
                placement="top-start"
                offsetOptions={{ mainAxis: 10, crossAxis: -5 }}>
                <TooltipTrigger asChild>
                  <Row alignItems="center" gap="gap_xs">
                    <Element typography="bodySmallAccent" color="secondary">
                      총 금액
                    </Element>
                    <Icon
                      name="FILL_INFORMATION_CIRCLE"
                      color="tertiary"
                      size="19px"
                    />
                  </Row>
                </TooltipTrigger>
                <TooltipContent fullWidth>
                  표시된 금액은 최종 금액이 아니며, 주문서에서 최종 금액을
                  확인해 주세요.
                </TooltipContent>
              </Tooltip>
              <AnimatedNumber
                value={totalSelectedAmount}
                typography="titleBase"
                color="primary"
              />
            </Row>
          }>
          {isAppWebView ? (
            <Button
              size="large"
              fullWidth
              onClick={handleCTAClick}
              disabled={!selectedId}>
              {isClass ? '이용권 구매하기' : '바로 구매'}
            </Button>
          ) : (
            <Row gap="gap_small">
              <Element flex={1}>
                <Button
                  variant="outlinePrimary"
                  size="large"
                  fullWidth
                  onClick={handleAddToCartClick}
                  disabled={selectedProducts.length === 0}>
                  장바구니 추가
                </Button>
              </Element>
              <Element flex={1}>
                <Button
                  size="large"
                  fullWidth
                  onClick={handleCTAClick}
                  disabled={selectedProducts.length === 0}>
                  {isClass ? '이용권 구매하기' : '바로 구매'}
                </Button>
              </Element>
            </Row>
          )}
        </CTA>
      </Column>
      <ConnectedVerificationCodeModal verification={verification} />
    </Column>
  );
};
